// @flow
/** @jsx jsx */

import * as React from 'react';

import Accordion from '@zapier/common-components/src/components/Accordion';
import Text from '@zapier/common-components/src/components/Text';
import { Colors } from '@zapier/theme';
import { createKeyHandler } from '@zapier/common-utils';
import { jsx } from '@emotion/core';

export type FaqQuestionType = {
  /** Answer to the FAQ */
  answer: React.Node,
  /** Id of the FAQ */
  id?: string,
  /** Question for the FAQ */
  question: React.Node,
};

type Props = {
  ...$Exact<FaqQuestionType>,
  /** Is default open */
  isTarget?: boolean,
  /** Use Horizontal Padding inside FAQ */
  useHorizontalPadding?: boolean,
};

const accordionCss = {
  borderBottom: `1px solid ${Colors.adamantium}`,
  color: Colors.batmobile,
};

const bodyCss = (useHorizontalPadding: boolean) => ({
  padding: useHorizontalPadding ? '0 15px' : 0,
  margin: '20px 0',
});

const headerCss = (useHorizontalPadding: boolean) => ({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  outlineColor: Colors.adamantium,
  padding: `16px ${useHorizontalPadding ? 15 : 0}px`,
  userSelect: 'none',
});

const FaqQuestion = ({
  answer,
  id,
  isTarget,
  question,
  useHorizontalPadding = false,
}: Props) => {
  return (
    <div css={accordionCss} id={id}>
      <Accordion isOpen={isTarget}>
        <Accordion.Header>
          {({
            isOpen,
            toggleAccordion,
          }: {
            isOpen: boolean,
            toggleAccordion: () => void,
          }) => (
            <div
              css={headerCss(useHorizontalPadding)}
              onClick={toggleAccordion}
              onKeyDown={createKeyHandler(['enter'], toggleAccordion)}
              role="button"
              tabIndex="0"
            >
              <Text type="body-large-strong">{question}</Text>
              <Accordion.Arrow isOpen={isOpen} />
            </div>
          )}
        </Accordion.Header>
        <Accordion.Body>
          <div css={bodyCss(useHorizontalPadding)}>
            <Text type="body">{answer}</Text>
          </div>
        </Accordion.Body>
      </Accordion>
    </div>
  );
};

export default FaqQuestion;
